/** @jsxImportSource @emotion/react */
import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import { Link } from "@packages/framework";
import { differenceInHours } from "date-fns";

/** Avoid circular dependency */
import { Matrix, Stats } from "../../../../../../../games/src/games/categories";
import {
  type CSSColorStrict,
  Text,
  type ThemeType,
} from "../../../../../../emotion";
import { Button } from "../../../_core/Button/Button.emotion";
import { TrophyIcon } from "../../../icons/Icon/GeneralIcons";
import type { CategoriesGameMediumProps } from "../../categories-game/CategoriesGameMedium";
import { CategoriesGameShareIconButton } from "../../categories-game/CategoriesGameShareIconButton";
import type { ContentMediumProps } from "../../content/ContentMedium/ContentMedium.emotion";
import type { BaseGetSectionItemProps } from "./Section.emotion";

const cssButton: Interpolation = {
  left: "50%",
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1,
};
const cssDivIsFinishedContentContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  color: theme.colors.white,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.l,
  height: "100%",
  justifyContent: "space-between",
  padding: theme.spacing.l,
});
const cssDivContentContainerGroupContainer: Interpolation<ThemeType> = (
  theme,
) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing.s,
  justifyContent: "space-between",
  width: "100%",
});
const cssDivMatrixContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.m,
});
const cssMask: Interpolation = {
  display: "none",
};
const cssTextBody: Interpolation<ThemeType> = (theme) => ({
  color: theme.tints.tint80,
  textAlign: "center",
});

export const getSmallSectionItemCategoriesProps = ({
  analytics,
  item,
  screenName,
  section,
  t,
}: BaseGetSectionItemProps): ContentMediumProps => {
  const isFinished = item?.reference?.state?.state === "FINISHED";
  const isWon = item?.reference?.state?.summary?.won;

  const onClick = () => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedSectionItem,
      properties: {
        reference_type: "categories_game",
        screen_name: screenName ?? null,
        section_title: section?.title ?? null,
      },
    });
  };

  return {
    title: t("games_categories"),
    description: isWon
      ? t("the_word_solved_in_x_tries", {
          the_word_solved_in_x_tries:
            item?.reference?.state?.summary?.matrix?.length,
        })
      : isFinished
        ? t("try_game_again_tomorrow")
        : t("game_resets_in_hours", {
            game_resets_in_hours: differenceInHours(
              new Date(item?.reference?.game?.ends_at),
              new Date(),
            ),
          }),
    metadata: t("games_underline"),
    metadataInlineIcon: <TrophyIcon.Inline />,
    imgSrc: item?.reference?.game?.images,
    asChild: true,
    children: (
      <Link
        href={`${process.env.DOPPLER_PUBLIC_GAMES_URL}/categories?source=homepage`}
        onClick={onClick}
        rel="noopener noreferrer"
        target="_blank"
      />
    ),
    imgOverlay: isFinished ? null : (
      <Button css={cssButton} variant="secondary">
        {t("button_description_play")}
      </Button>
    ),
  };
};

export const getSectionItemCategoriesGameProps = ({
  analytics,
  item,
  screenName,
  section,
  t,
}: BaseGetSectionItemProps): CategoriesGameMediumProps => {
  const isFinished = item?.reference?.state?.state === "FINISHED";
  const isUnstarted = item?.reference?.state?.state === "UNSTARTED";
  const isWon = item?.reference?.state?.summary?.won ?? false;

  const onClick = () => {
    analytics.track({
      event: isUnstarted
        ? HallowAnalyticsEvent.TappedStartCategories
        : isFinished
          ? HallowAnalyticsEvent.TappedCategoriesSummary
          : HallowAnalyticsEvent.TappedResumeCategories,
      properties: {
        game_id: item?.reference?.game?.id,
        word: item?.reference?.game?.word,
        word_id: item?.reference?.game?.id,
      },
    });
    analytics.track({
      event: HallowAnalyticsEvent.TappedSectionItem,
      properties: {
        reference_type: "categories_game",
        screen_name: screenName ?? null,
        section_title: section?.title ?? null,
      },
    });
  };

  const centerActions = !isFinished
    ? [
        <Button key="links" variant="white">
          {t(isUnstarted ? "games_play" : "games_resume")}
        </Button>,
      ]
    : null;

  const tags = item?.reference?.game
    ? [
        t("x_playing_plural", {
          x_playing_plural: item.reference.game.player_count,
        }),
        t("ends_in_hours", {
          ends_in_hours: differenceInHours(
            new Date(item.reference.game.ends_at),
            new Date(),
          ),
        }),
      ]
    : null;

  return {
    asChild: true,
    centerActions: isFinished ? null : centerActions,
    children: (
      <Link
        href={`${process.env.DOPPLER_PUBLIC_GAMES_URL}/categories?source=homepage`}
        rel="noopener noreferrer"
        target="_blank"
      />
    ),
    color: item?.reference?.game?.images?.color_hex as CSSColorStrict,
    content: isFinished ? (
      <div css={cssDivIsFinishedContentContainer}>
        <div css={cssDivContentContainerGroupContainer}>
          <Text size="l" type="title">
            <I18nMessage message="categories_game_finished_title" />
          </Text>
          <CategoriesGameShareIconButton game={item?.reference} />
        </div>
        <div css={cssDivMatrixContainer}>
          <Matrix matrix={item?.reference?.state?.summary?.matrix} />
          <Text css={cssTextBody} size="m" type="body">
            <I18nMessage
              message={
                isWon
                  ? "games_you_solved_todays_puzzle"
                  : "categories_game_failure_description"
              }
            />
          </Text>
        </div>
        <Stats size="m" summary={item?.reference?.state?.summary} />
      </div>
    ) : null,
    description: isFinished ? null : t("games_categories_subtitle"),
    imgSrc: isFinished ? null : item?.reference?.game?.images,
    maskCSS: isFinished ? cssMask : undefined,
    onClick: onClick,
    tags: isFinished ? null : tags,
    title: isFinished ? null : t("games_categories"),
  };
};
